/**
 * HealthSummaryCard.jsx — iter894 (Feb 2026)
 *
 * Ново място в /profile страницата: агрегира ключовите числа от
 * здравното досие в един бърз поглед.
 *
 * Секции:
 *   • Антропометрия — height, weight, BMI, waist, body-fat
 *   • Метаболитен бюджет — BMR, TDEE, дневни калории, макроси
 *   • Персонализиран DIL — 14-дневна средна стойност + target
 *   • Twin scores — VAIS (visceral adiposity), Metabolic, Hormonal (когато има)
 *
 * Всичките числа са read-only screenshots. За редакция → бутонът
 * „Редактирай профил" в горната част на страницата.
 *
 * Data flow:
 *   GET /api/user/health-report/latest      → BMR/TDEE + макроси + goal
 *   GET /api/me/vais                        → VAIS score
 *   GET /api/tracking/dil-summary?days=14   → mean DIL (реалната стойност)
 *   auth.user.profile                       → anthropometrics fallback
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Ruler, Flame, Target, Utensils, Activity, Sparkles, HeartPulse, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import MacroDilReconciliationCard from "@/components/profile/MacroDilReconciliationCard";  // iter936
import PerinatalDilBadge from "@/components/profile/PerinatalDilBadge";  // iter1108
import { toast } from "sonner";

const CANONICAL = {
  bg: {
    title: "🩺 Здравно резюме",
    subtitle: "Обобщение на ключовите числа от Вашето досие",
    anthropometry: "Антропометрия",
    metabolic:     "Метаболитен бюджет",
    dil:           "Диетичен инсулинов товар (DIL)",
    twins:         "Twin индекси",
    height:        "Ръст",
    weight:        "Тегло",
    bmi:           "BMI",
    waist:         "Обиколка на кръст",
    bodyFat:       "Телесни мазнини",
    bmr:           "BMR",
    tdee:          "TDEE",
    calories:      "Дневни kcal",
    protein:       "Протеин",
    carbs:         "Въглехидрати",
    fat:           "Мазнини",
    goal:          "Цел",
    activity:      "Активност",
    dilAvg:        "14-дневна средна",
    dilTarget:     "Оптимална зона",
    vais:          "VAIS (висцерална мазнина)",
    empty:         "Няма данни. Попълнете профила и качете лабораторни резултати.",
  },
  en: {
    title: "🩺 Health summary",
    subtitle: "The key numbers from your health record at a glance",
    anthropometry: "Anthropometry",
    metabolic:     "Metabolic budget",
    dil:           "Dietary Insulin Load (DIL)",
    twins:         "Twin indices",
    height: "Height", weight: "Weight", bmi: "BMI",
    waist: "Waist", bodyFat: "Body fat",
    bmr: "BMR", tdee: "TDEE",
    calories: "Daily kcal", protein: "Protein", carbs: "Carbs", fat: "Fat",
    goal: "Goal", activity: "Activity",
    dilAvg: "14-day average", dilTarget: "Optimal zone",
    vais: "VAIS (visceral adiposity)",
    empty: "No data yet. Complete your profile and upload labs.",
  },
};

function Pill({ Icon, label, value, unit, isDark, testid }) {
  // iter935 — Fix: previously used `!Number.isNaN(Number(value))` which
  // rejected any string value like "Загуба на тегло" or "Умерена" because
  // Number("Загуба на тегло") = NaN → the goal/activity pills always
  // rendered "—" even when a valid label was supplied. Now we accept any
  // non-empty scalar and only reject NaN numerics.
  const hasValue = value != null && value !== ""
                   && !(typeof value === "number" && Number.isNaN(value));
  return (
    <div
      className={`rounded-xl p-2 border ${
        isDark ? "bg-white/[0.03] border-white/5" : "bg-white/70 border-gray-200"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1 mb-0.5">
        {Icon && <Icon className={`w-3 h-3 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />}
        <span
          className={`text-[9px] uppercase tracking-wider font-semibold ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {label}
        </span>
      </div>
      <div className={`text-sm font-bold tabular-nums ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
        {hasValue ? (
          <>
            {typeof value === "number" ? Math.round(value * 10) / 10 : value}
            {unit && (
              <span className={`text-[9px] font-normal ml-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {unit}
              </span>
            )}
          </>
        ) : (
          <span className={`text-xs font-normal ${isDark ? "text-gray-500" : "text-gray-400"}`}>—</span>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ Icon, title, isDark }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className={`w-3.5 h-3.5 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
      <h3
        className={`text-[10px] uppercase tracking-wider font-bold ${
          isDark ? "text-emerald-300" : "text-emerald-700"
        }`}
      >
        {title}
      </h3>
    </div>
  );
}

export default function HealthSummaryCard() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = CANONICAL[language] || CANONICAL.bg;

  const [report, setReport] = useState(null);
  const [vais, setVais] = useState(null);
  const [dil, setDil] = useState(null);
  const [dilBreakdown, setDilBreakdown] = useState(null);   // iter936
  const [dilGap, setDilGap] = useState(null);               // iter1100 — CTA signal
  const [freshProfile, setFreshProfile] = useState(null);   // iter935 — up-to-date profile
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!API) return;
    const results = await Promise.allSettled([
      axios.get(`${API}/user/health-report/latest`),
      axios.get(`${API}/me/vais`),
      axios.get(`${API}/health-intelligence/summary?days=14`).catch(() => null),
      axios.get(`${API}/auth/me`).catch(() => null),
    ]);
    if (results[0].status === "fulfilled") setReport(results[0].value.data?.report || null);
    if (results[1].status === "fulfilled") setVais(results[1].value.data || null);
    if (results[2].status === "fulfilled" && results[2].value) {
      const hi = results[2].value.data || {};
      setDil({
        dil_avg:    hi?.metabolic?.dil_avg           ?? null,
        dil_target: hi?.metabolic?.dil_personal_target ?? null,
      });
      setDilBreakdown(hi?.metabolic?.dil_breakdown || null);
      setDilGap(hi?.metabolic?.dil_gap || null);
    }
    if (results[3].status === "fulfilled" && results[3].value) {
      setFreshProfile(results[3].value.data?.profile
                     || results[3].value.data?.user?.profile
                     || null);
    }
    setLoading(false);
  }, [API]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await fetchAll(); })();
    return () => { cancelled = true; };
  }, [fetchAll]);

  const handleRegenerate = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/user/health-report/regenerate`, { language });
      if (data?.report) {
        toast.success(language === "bg" ? "AI-докладът е прегенериран ✓" : "AI report regenerated ✓");
        await fetchAll();
      } else {
        toast.info(language === "bg" ? "Няма нови данни за прегенериране" : "No new data to regenerate");
      }
    } catch (e) {
      toast.error(language === "bg" ? "Грешка при прегенериране" : "Regenerate failed");
    }
  }, [API, language, fetchAll]);

  // Extract anthropometrics from either the report snapshot or auth profile.
  // iter933 — Added `profile.body.height/weight` fallback so values saved
  // via ProfileEditModal (which persists into `profile.body`) are shown
  // in the Health Summary. Previously they landed in the DB but the
  // Summary card only looked at `biology`/`measurements` paths → "—".
  // iter935 — Extended fallbacks to cover BOTH `_cm`/`_kg` and plain keys
  // + `measurements.weight_kg` (written by ProfileEditModal but was skipped).
  const anthro = useMemo(() => {
    const p = freshProfile || user?.profile || {};
    const meas = p.measurements || {};
    const bio  = p.biology || p.basic || {};
    const body = p.body || {};                       // iter933
    const rep  = report?.standard_metrics || {};
    const height = bio.height_cm || meas.height_cm || meas.height
                 || body.height_cm || body.height || p.height;
    const weight = bio.weight_kg || meas.weight_kg || meas.weight
                 || body.weight_kg || body.weight || p.weight;
    return {
      height,
      weight,
      bmi:      rep.bmi     || (height && weight ? Math.round((weight / ((height / 100) ** 2)) * 10) / 10 : null),
      waist:    meas.waist_cm || meas.waist || body.waist_cm || body.waist,
      body_fat: rep.bodyFat  || meas.body_fat_pct || meas.body_fat_percent,
    };
  }, [user, freshProfile, report]);

  // iter935 — Goal & Activity fallback chain. Prefer the FRESH profile
  // fields (from /api/auth/me) over the cached report — the report can
  // be months old, whereas profile.lifestyle is edited real-time via
  // the ProfileEditModal. Only fall back to report.goal if profile is
  // truly empty. Convert canonical enum ids into Bulgarian labels.
  const _profile = freshProfile || user?.profile || {};
  const goalRaw = _profile?.lifestyle?.primaryGoal
               || _profile?.goal
               || _profile?.primary_goal
               || report?.goal
               || null;
  const activityRaw = _profile?.lifestyle?.activityLevel
                   || _profile?.activityLevel
                   || report?.activity
                   || null;

  const GOAL_LABELS_BG = {
    weight_loss:    "Загуба на тегло",
    fat_loss:       "Загуба на мазнини",
    muscle_gain:    "Мускулна маса",
    weight_gain:    "Наддаване",
    maintenance:    "Поддържане",
    maintain:       "Поддържане",
    general_health: "Общо здраве",
    performance:    "Спортна форма",
    longevity:      "Дълголетие",
    hormonal_balance:"Хормонален баланс",
  };
  const ACTIVITY_LABELS_BG = {
    sedentary:      "Заседнал",
    light:          "Лека",
    moderate:       "Умерена",
    active:         "Активна",
    very_active:    "Много активна",
    athlete:        "Спортист",
  };
  const goalLabel = language === "bg"
    ? (GOAL_LABELS_BG[goalRaw] || goalRaw)
    : goalRaw;
  const activityLabel = language === "bg"
    ? (ACTIVITY_LABELS_BG[activityRaw] || activityRaw)
    : activityRaw;

  const metrics = report?.standard_metrics || {};
  const hasAny = report || vais || dil || anthro.height || anthro.weight;

  const cardBase = isDark
    ? "bg-gradient-to-br from-emerald-950/25 via-[hsl(222,47%,10%)] to-teal-900/15 border-emerald-500/25"
    : "bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-emerald-200";

  return (
    <section
      className={`rounded-2xl border p-5 ${cardBase}`}
      data-testid="profile-health-summary-card"
    >
      <div className="flex items-start gap-2 mb-4">
        <HeartPulse className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-extrabold flex items-center gap-2 flex-wrap ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            <span>{t.title}</span>
            <PerinatalDilBadge testid="hs-perinatal-badge" />
          </div>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {t.subtitle}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs opacity-70" data-testid="profile-health-summary-loading">
          …
        </div>
      ) : !hasAny ? (
        <div className="py-6 text-center" data-testid="profile-health-summary-empty">
          <p className={`text-xs mb-3 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {t.empty}
          </p>
          <a
            href={window.location.pathname.startsWith("/dashboard") ? "/dashboard/profile-wizard" : "/onboarding"}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
              isDark ? "bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"
                     : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
            data-testid="profile-health-summary-cta"
          >
            <HeartPulse className="w-3.5 h-3.5" />
            {language === "bg" ? "Попълни профила и генерирай доклад" : "Complete profile & generate report"}
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Anthropometry */}
          <div>
            <SectionHeader Icon={Ruler} title={t.anthropometry} isDark={isDark} />
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Pill Icon={Ruler}    label={t.height}  value={anthro.height} unit="cm" isDark={isDark} testid="hs-anthro-height" />
              <Pill Icon={Ruler}    label={t.weight}  value={anthro.weight} unit="kg" isDark={isDark} testid="hs-anthro-weight" />
              <Pill Icon={Activity} label={t.bmi}     value={anthro.bmi}    unit=""   isDark={isDark} testid="hs-anthro-bmi"    />
              <Pill Icon={Ruler}    label={t.waist}   value={anthro.waist}  unit="cm" isDark={isDark} testid="hs-anthro-waist"  />
              <Pill Icon={Activity} label={t.bodyFat} value={anthro.body_fat} unit="%" isDark={isDark} testid="hs-anthro-bodyfat" />
            </div>
          </div>

          {/* Metabolic budget */}
          <div>
            <SectionHeader Icon={Flame} title={t.metabolic} isDark={isDark} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Pill Icon={Flame}    label={t.bmr}      value={metrics.bmr}      unit="kcal" isDark={isDark} testid="hs-metric-bmr" />
              <Pill Icon={Target}   label={t.tdee}     value={metrics.tdee}     unit="kcal" isDark={isDark} testid="hs-metric-tdee" />
              <Pill Icon={Utensils} label={t.calories} value={metrics.calories} unit="kcal" isDark={isDark} testid="hs-metric-calories" />
              <Pill Icon={Utensils} label={t.protein}  value={metrics.protein}  unit="g"    isDark={isDark} testid="hs-metric-protein" />
              <Pill Icon={Utensils} label={t.carbs}    value={metrics.carbs}    unit="g"    isDark={isDark} testid="hs-metric-carbs" />
              <Pill Icon={Utensils} label={t.fat}      value={metrics.fat}      unit="g"    isDark={isDark} testid="hs-metric-fat" />
              <Pill Icon={Sparkles} label={t.goal}     value={goalLabel     || null} unit="" isDark={isDark} testid="hs-metric-goal" />
              <Pill Icon={Activity} label={t.activity} value={activityLabel || null} unit="" isDark={isDark} testid="hs-metric-activity" />
            </div>

            {/* iter936 — Macro ↔ DIL reconciliation (educational, science-based).
                Replaces the earlier one-liner commentary with a full 3-tier basket
                comparison, the deterministic formula strip, and a "Прегенерирай"
                CTA when profile data has drifted from the cached AI report. */}
            <MacroDilReconciliationCard
              breakdown={dilBreakdown}
              gap={dilGap}
              macros={metrics}
              dilAvg={dil?.dil_avg}
              onRegenerate={handleRegenerate}
              onRefresh={fetchAll}
            />
          </div>

          {/* DIL — iter935: show whenever either 14-day avg or personal target exists */}
          {(dil?.dil_avg != null || dil?.dil_target != null) && (
            <div>
              <SectionHeader Icon={TrendingUp} title={t.dil} isDark={isDark} />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Pill Icon={TrendingUp} label={t.dilAvg}    value={dil?.dil_avg}    unit="g" isDark={isDark} testid="hs-dil-avg" />
                <Pill Icon={Target}     label={t.dilTarget} value={dil?.dil_target} unit="g" isDark={isDark} testid="hs-dil-target" />
                {dil?.dil_avg != null && dil?.dil_target != null && (
                  <div
                    className={`rounded-xl p-2 border ${
                      dil.dil_avg <= dil.dil_target
                        ? (isDark ? "bg-emerald-500/10 border-emerald-400/30" : "bg-emerald-50 border-emerald-200")
                        : (isDark ? "bg-amber-500/10 border-amber-400/30"    : "bg-amber-50 border-amber-200")
                    }`}
                    data-testid="hs-dil-delta"
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <TrendingUp className={`w-3 h-3 ${
                        dil.dil_avg <= dil.dil_target
                          ? (isDark ? "text-emerald-300" : "text-emerald-600")
                          : (isDark ? "text-amber-300"   : "text-amber-600")
                      }`} />
                      <span className={`text-[9px] uppercase tracking-wider font-semibold ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}>
                        Спрямо целта
                      </span>
                    </div>
                    <div className={`text-sm font-bold tabular-nums ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {dil.dil_avg <= dil.dil_target ? "✓ в цел" : `+${Math.round(dil.dil_avg - dil.dil_target)}g над цел`}
                    </div>
                  </div>
                )}
              </div>
              <p className={`mt-2 text-[10px] leading-snug italic ${isDark ? "text-gray-400" : "text-gray-600"}`} data-testid="hs-dil-caption">
                14-дневната средна DIL се сравнява с личния Ви таван (изчислен от тегло, цел и хормонален контекст).
                {dil?.dil_target != null && dil?.dil_avg != null && dil.dil_avg > dil.dil_target && (
                  <> При превишение — увеличете фибрите и полифенолите, редуцирайте бързите въглехидрати около вечерта.</>
                )}
              </p>
            </div>
          )}

          {/* Twin indices */}
          {vais?.vas_0_100 != null && (
            <div>
              <SectionHeader Icon={HeartPulse} title={t.twins} isDark={isDark} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                  className={`rounded-xl p-3 border ${
                    isDark ? "bg-white/[0.03] border-white/5" : "bg-white/70 border-gray-200"
                  }`}
                  data-testid="hs-twin-vais"
                >
                  <div className="text-[9px] uppercase tracking-wider font-semibold mb-1 opacity-70">
                    {t.vais}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold tabular-nums" style={{ color: vais.color || undefined }}>
                      {Math.round(vais.vas_0_100)}
                    </span>
                    <span className="text-[10px] opacity-70">/ 100</span>
                    <span
                      className="text-[10px] font-semibold ml-1"
                      style={{ color: vais.color || undefined }}
                    >
                      {language === "bg" ? vais.tier_bg : vais.tier_en}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
